/**
 * StageList — the grey stage list on the left of Admin > Stage Pipeline
 * (FTP's .stages-list-container): a "+" at the top adds a stage, Inquiry sits
 * first and never moves, and every other live stage drags by its grip to set
 * the pipeline order (the CRM board's columns, every plan's progress bar).
 * Clicking a stage selects it. "Show deleted" lists the deleted stages under
 * the live ones, each with Restore.
 *
 * Below lg the page renders it inside the off-canvas "Stages" panel instead
 * of inline (L3). There it drops its fixed width and divider and fills the
 * panel, which scrolls it; content and drag are the same in both places.
 *
 * On a Locked page (plan 2026-10-10-001, U7) a stage still selects and Show
 * deleted still lists, but there is no "+", grip, drag or Restore.
 */
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Lock, Plus } from 'lucide-react';
import { AdminRowName } from '@/components/admin/AdminList';
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useAdminDragSensors } from '@/components/admin/SortableAdminRow';
import { useInOffCanvas } from '@/components/layout/OffCanvasPanel';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { cn } from '@/lib/utils';
import type { PipelineStage } from '@/services/stagePipelineAdmin.service';
import { STAGE_LIST_DND_ID } from './pillDnd';

export interface StageListProps {
  /** The live stages in pipeline order (Inquiry first). */
  stages: PipelineStage[];
  /** The deleted stages, shown with Show deleted. */
  deleted: PipelineStage[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onAdd: () => void;
  /** The live stages after Inquiry, in their new order. */
  onReorder: (ids: number[]) => void;
  onRestore: (stage: PipelineStage) => void;
  showDeleted: boolean;
  onShowDeletedChange: (on: boolean) => void;
  className?: string;
}

const ROW_CLASS = 'relative flex h-10 items-center rounded-full text-xs transition-colors';

function rowTone(selected: boolean): string {
  return selected ? 'bg-[#3a7fd5] text-white' : 'bg-white text-slate-600 hover:bg-slate-200 hover:text-slate-900';
}

function StageName({ stage, onSelect }: { stage: PipelineStage; onSelect: (id: number) => void }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(stage.id)}
      className="h-full min-w-0 flex-1 truncate rounded-full pr-8 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
    >
      {stage.name}
    </button>
  );
}

function SortableStageRow({
  stage,
  selected,
  onSelect,
  canDrag,
}: {
  stage: PipelineStage;
  selected: boolean;
  onSelect: (id: number) => void;
  canDrag: boolean;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
    disabled: !canDrag,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      data-testid="stage-row"
      data-stage-id={stage.id}
      data-stage-name={stage.name}
      aria-current={selected ? 'true' : undefined}
      className={cn(ROW_CLASS, rowTone(selected), isDragging && 'z-10 shadow-md')}
    >
      {canDrag && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          className={cn(
            'absolute left-1.5 flex h-7 w-6 cursor-grab touch-none items-center justify-center rounded-full active:cursor-grabbing',
            selected ? 'text-white/70 hover:text-white' : 'text-slate-300 hover:text-slate-500',
          )}
          aria-label={`Drag to reorder ${stage.name}`}
          title="Drag to reorder"
        >
          <GripVertical className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      <span className="w-8 shrink-0" aria-hidden />
      <StageName stage={stage} onSelect={onSelect} />
    </div>
  );
}

export function StageList({
  stages,
  deleted,
  selectedId,
  onSelect,
  onAdd,
  onReorder,
  onRestore,
  showDeleted,
  onShowDeletedChange,
  className,
}: StageListProps) {
  const sensors = useAdminDragSensors();
  const inPanel = useInOffCanvas();
  const locked = useAdminPageLevel() === 'locked';
  const inquiry = stages.find((s) => s.isInquiry);
  const movable = stages.filter((s) => !s.isInquiry);
  const ids = movable.map((s) => s.id);

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (locked || !over || active.id === over.id) return;
    const from = ids.indexOf(Number(active.id));
    const to = ids.indexOf(Number(over.id));
    // Inquiry is not in the sortable list, so nothing can take its place.
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(ids, from, to));
  }

  return (
    <section
      data-testid="stage-list"
      aria-label="Stages"
      className={cn(
        'flex flex-col bg-slate-100',
        // Inline: FTP's 200px column. In the panel: the panel's width and height, the footer at the bottom.
        inPanel ? 'min-h-full w-full' : 'w-[200px] shrink-0 border-r border-slate-200',
        className,
      )}
    >
      {!locked && (
        <div className="flex shrink-0 justify-center py-2">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Add stage"
            title="Add stage"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={onAdd}
            className="rounded-full text-slate-500 hover:bg-white"
          />
        </div>
      )}

      <div className={cn('relative min-h-0 flex-1 overflow-y-auto px-2 pb-2', locked && 'pt-2')}>
        <div className="flex flex-col gap-1">
          {inquiry && (
            <div
              data-testid="stage-row"
              data-stage-id={inquiry.id}
              data-stage-name={inquiry.name}
              aria-current={inquiry.id === selectedId ? 'true' : undefined}
              className={cn(ROW_CLASS, rowTone(inquiry.id === selectedId))}
            >
              <span
                className={cn('flex w-8 shrink-0 justify-center pl-1.5', inquiry.id === selectedId ? 'text-white/70' : 'text-slate-300')}
                title="Inquiry is always first"
              >
                <Lock className="h-3 w-3" aria-hidden />
              </span>
              <StageName stage={inquiry} onSelect={onSelect} />
            </div>
          )}
          {locked ? (
            // Locked: nothing drags, so no drag context (or its drag instructions) at all.
            movable.map((s) => <SortableStageRow key={s.id} stage={s} selected={s.id === selectedId} onSelect={onSelect} canDrag={false} />)
          ) : (
            <DndContext id={STAGE_LIST_DND_ID} sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {movable.map((s) => (
                  <SortableStageRow key={s.id} stage={s} selected={s.id === selectedId} onSelect={onSelect} canDrag />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>

        {showDeleted && deleted.length > 0 && (
          <div className="mt-3 flex flex-col gap-1 border-t border-slate-200 pt-3">
            {deleted.map((s) => (
              <div
                key={s.id}
                data-testid="deleted-stage-row"
                data-stage-id={s.id}
                data-stage-name={s.name}
                className="flex min-h-10 items-center gap-1 rounded-md bg-amber-50/60 py-1 pl-3 pr-1 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <AdminRowName deleted tone="child">
                    {s.name}
                  </AdminRowName>
                </div>
                <AdminRowAction kind="restore" label={`Restore ${s.name}`} onClick={() => onRestore(s)} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-200 px-3 py-2">
        <label htmlFor="stage-pipeline-show-deleted" className="cursor-pointer select-none text-xs text-slate-500">
          Show deleted
        </label>
        <Switch id="stage-pipeline-show-deleted" checked={showDeleted} onChange={onShowDeletedChange} />
      </div>
    </section>
  );
}
